import { createSlice } from "@reduxjs/toolkit";

export const initialState = {
  vendorStats: {
    total_properties: 0,
    active_properties: 0,
    total_bookings: 0,
    active_bookings: 0,
    total_revenue: "0",
    monthly_revenue: 0,
    average_rating: 0
  },
  vendorAmenities:{},
  roomAmenities: {},
  propertyTypes: [],
  propertyTypesLoading: false,
  roomTypes: [],
  bedTypes: [],
  vendorPropertyTaxesFees: [],
  vendorPropertyCompletionStatus: null,
  vendorActiveStatus: null,
  vendorDashboard: {
    vendor_profile: null,
    stats: {
      total_properties: 0,
      active_properties: 0,
      pending_properties: 0,
      total_rooms: 0,
      total_bookings: 0,
      pending_bookings: 0,
      confirmed_bookings: 0,
      revenue_this_month: 0,
      revenue_last_month: "0",
      total_revenue: "0",
      average_rating: "0",
      total_reviews: 0,
      revenue_growth: 0
    },
    recent_bookings: [],
    today_checkins: [],
    tomorrow_checkouts: [],
    property_performance: [],
    revenue_trend: []
  },
  propertyCreation: {
    success: false,
    data: null,
    message: ""
  },
  propertyCreationPolicies: {
    success: false,
    data: null,
    message: ""
  },
  propertyCreationAmenities: {
    success: false,
    data: null,
    message: ""
  },
  roomAmenitiesCreation: {
    success: false,
    data: null,
    message: ""
  },
  topSellingProperty: {},
  allVendorProperty: {},
  topSellingRoom: [],
  vendorPropertyStats: {
    properties: [],
    pagination: {
      current_page: 1,
      per_page: 15,
      total: 0,
      last_page: 1,
      from: 0,
      to: 0
    },
    filters: {
      sort_options: []
    }
  },
  propertyCompletionMap: {} as Record<number, any>,
  propertyDataMap: {} as Record<number, any>,
  propertyTaxesFeesMap: {} as Record<number, any>,
  propertyServicesMap: {} as Record<number, any>,
  propertyContractMap: {} as Record<number, any>,
  error: "",
  loading: false,
  errorMsg: false,
};

const vendorSlice = createSlice({
  name: "vendor",
  initialState,
  reducers: {
    apiError(state, action) {
      state.error = action.payload;
      state.loading = false;
      state.errorMsg = true;
    },
    getVendorStatsSuccess(state, action) {
      // Extract only the data portion if the response has a 'data' property
      state.vendorStats = action.payload.data || action.payload;
      state.loading = false;
      state.errorMsg = false;
    },
    getVendorPropertiesAmenitiesSuccess(state, action) {
      // Extract only the data portion if the response has a 'data' property
      state.vendorAmenities = action.payload.data || action.payload;
      state.loading = false;
      state.errorMsg = false;
    },
    getRoomAmenitiesSuccess(state, action) {
      state.roomAmenities = action.payload.data || action.payload;
      state.loading = false;
      state.errorMsg = false;
    },
    getPropertyTypesSuccess(state, action) {
      state.propertyTypes = action.payload.data || action.payload;
      state.propertyTypesLoading = false;
      state.errorMsg = false;
    },
    setPropertyTypesLoading(state, action) {
      state.propertyTypesLoading = action.payload;
    },
    getRoomTypesSuccess(state, action) {
      state.roomTypes = action.payload.data || action.payload;
      state.loading = false;
      state.errorMsg = false;
    },
    getBedTypesSuccess(state, action) {
      state.bedTypes = action.payload.data || action.payload;
      state.loading = false;
      state.errorMsg = false;
    },
    getVendorDashboardSuccess(state, action) {
      // Store the complete dashboard data
      state.vendorDashboard = action.payload.data || action.payload;
      state.loading = false;
      state.errorMsg = false;
    },
    getTopSellingPropertiesSuccess(state, action) {
      state.topSellingProperty = action.payload || action.payload;
      state.loading = false;
      state.errorMsg = false;
    },
    getAllVendorPropertiesSuccess(state, action) {
      state.allVendorProperty = action.payload || action.payload;
      state.loading = false;
      state.errorMsg = false;
    },
    getTopSellingRoomsSuccess(state, action) {
      state.topSellingRoom = action.payload || action.payload;
      state.loading = false;
      state.errorMsg = false;
    },
    getVendorPropertySuccess(state, action) {
      const p = action.payload;
      if (p?.id != null) {
        state.propertyDataMap[p.id] = p;
      }
    },
    getVendorPropertyStatsSuccess(state, action) {
      state.vendorPropertyStats = action.payload;
      // Build a map of propertyId -> completion_status for use in detail pages
      const properties: any[] = action.payload?.properties || [];
      properties.forEach((p: any) => {
        if (p.property_id != null) {
          if (p.completion_status) {
            state.propertyCompletionMap[p.property_id] = p.completion_status;
          }
          state.propertyDataMap[p.property_id] = p;
        }
      });
      state.loading = false;
      state.errorMsg = false;
    },
    getVendorPropertyTaxesFeesSuccess(state, action) {
      state.vendorPropertyTaxesFees = action.payload.data || action.payload;
      state.loading = false;
      state.errorMsg = false;
    },
    getVendorPropertyTaxesFeesDetailSuccess(state, action) {
      const { propertyId, data } = action.payload;
      if (propertyId != null) {
        state.propertyTaxesFeesMap[Number(propertyId)] = data;
      }
      state.loading = false;
      state.errorMsg = false;
    },
    getVendorPropertyServicesSuccess(state, action) {
      const { propertyId, data } = action.payload;
      if (propertyId != null) {
        state.propertyServicesMap[Number(propertyId)] = data;
      }
      state.loading = false;
      state.errorMsg = false;
    },
    getVendorPropertyContractSuccess(state, action) {
      const { propertyId, data } = action.payload;
      if (propertyId != null) {
        state.propertyContractMap[Number(propertyId)] = data;
      }
      state.loading = false;
      state.errorMsg = false;
    },
    getVendorPropertyCompletionStatusSuccess(state, action) {
      const data = action.payload.data || action.payload;
      state.vendorPropertyCompletionStatus = data;
      // Also cache in the map so detail pages can read it after a reload
      if (data?.property_id != null) {
        state.propertyCompletionMap[data.property_id] = data;
      }
      state.loading = false;
      state.errorMsg = false;
    },
    getVendorActiveStatusSuccess(state, action) {
      state.vendorActiveStatus = action.payload.data || action.payload;
      state.loading = false;
      state.errorMsg = false;
    },
    postVendorPropertiesSuccess(state, action) {
      state.propertyCreation.success = true;
      state.propertyCreation.data = action.payload.data || action.payload;
      state.propertyCreation.message = action.payload.message || "Property created successfully";
      state.loading = false;
      state.errorMsg = false;
    },
    postVendorPropertiesPoliciesSuccess(state, action) {
      state.propertyCreationPolicies.success = true;
      state.propertyCreationPolicies.data = action.payload.data || action.payload;
      state.propertyCreationPolicies.message = action.payload.message || "Property created successfully";
      state.loading = false;
      state.errorMsg = false;
    },
    postVendorPropertiesAmenitiesSuccess(state, action) {
      state.propertyCreationAmenities.success = true;
      state.propertyCreationAmenities.data = action.payload.data || action.payload;
      state.propertyCreationAmenities.message = action.payload.message || "Property created successfully";
      state.loading = false;
      state.errorMsg = false;
    },
    postVendorRoomAmenitiesSuccess(state, action) {
      state.roomAmenitiesCreation.success = true;
      state.roomAmenitiesCreation.data = action.payload.data || action.payload;
      state.roomAmenitiesCreation.message = action.payload.message || "Room amenities updated successfully";
      state.loading = false;
      state.errorMsg = false;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    resetVendorFlag(state) {
      state.error = "";
      state.loading = false;
      state.errorMsg = false;
    },
    resetPropertyCreation(state) {
      state.propertyCreation = {
        success: false,
        data: null,
        message: ""
      };
    },
    resetPropertyCreationPolicies(state) {
      state.propertyCreation = {
        success: false,
        data: null,
        message: ""
      };
    },
    resetPropertyCreationAmenities(state) {
      state.propertyCreation = {
        success: false,
        data: null,
        message: ""
      };
    }
  },
});

export const {
  apiError,
  getVendorStatsSuccess,
  getVendorDashboardSuccess,
  postVendorPropertiesSuccess,
  getTopSellingPropertiesSuccess,
  postVendorPropertiesPoliciesSuccess,
  getTopSellingRoomsSuccess,
  getVendorPropertyStatsSuccess,
  getVendorPropertyTaxesFeesSuccess,
  getVendorPropertyTaxesFeesDetailSuccess,
  getVendorPropertyServicesSuccess,
  getVendorPropertyContractSuccess,
  getVendorPropertyCompletionStatusSuccess,
  getVendorActiveStatusSuccess,
  setLoading,
  resetVendorFlag,
  resetPropertyCreationPolicies,
  resetPropertyCreation,
  resetPropertyCreationAmenities,
  postVendorPropertiesAmenitiesSuccess,
  postVendorRoomAmenitiesSuccess,
  getVendorPropertiesAmenitiesSuccess,
  getRoomAmenitiesSuccess,
  getPropertyTypesSuccess,
  setPropertyTypesLoading,
  getRoomTypesSuccess,
  getBedTypesSuccess,
  getAllVendorPropertiesSuccess,
  getVendorPropertySuccess
} = vendorSlice.actions;

export default vendorSlice.reducer;
